/*
 * File: input-file.ts
 * File Created: 2024-02-17 08:47:15
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-10-14 09:28:11
 * Modified By: Val Liu
 * -----
 */

import { Component, Prop } from "vue-property-decorator";
import { BaseInputComponent } from "../core/lib";
import { PropInnateSync } from "@root/core/vue-props";
import { IInputFile, IInputFileUnitProcessing } from "./lib/core";
import { uniqueId } from "lodash";

interface IInputFileData {
    "text-addfile": string;
    "text-nofile": string;
    "text-notimagehint": string;
}

const defaultData: IInputFileData = {
    "text-addfile": "w_InputFileAddNew",
    "text-nofile": "w_InputFileNoFile",
    "text-notimagehint": "w_InputFileNotImageHint",
};

@Component
export default class InputFile extends BaseInputComponent {
    @PropInnateSync("value", { type: [Object, Array], default: undefined })
    innateValue!: IInputFile | IInputFile[] | undefined;

    @Prop({ type: String, default: "*/*" })
    contentType!: string;

    @Prop({ type: Object, required: false })
    declare data: IInputFileData;
    get mergedData() {
        return {
            ...defaultData,
            ...this.data,
        };
    }

    /// private helper
    // for easier to generate v-for contents.
    get generatedValue() {
        return this.innateValue == undefined
            ? []
            : Array.isArray(this.innateValue)
              ? this.innateValue
              : [this.innateValue];
    }

    // for removing file
    removeFile(item: IInputFile) {
        if ("loading" in item) return;
        if (!this.multiple) this.innateValue = undefined;
        else {
            this.innateValue = (this.innateValue as IInputFile[]).filter(
                (o: any) => o.id !== item.id,
            );
        }
    }

    // download file
    downloadFile(item: IInputFile) {
        if ("loading" in item) return;
        const url = this.getImageUrl(item);
        const link = document.createElement("a");
        link.href = url;
        link.download = item.originalName || "downloaded-file";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    isImageFile(item: IInputFile): boolean {
        if ("loading" in item) return false;
        return /^image\//.test(item.contentType);
    }

    // load image.
    getImageUrl(value) {
        return `${this.$server.UrlFile()}?id=${value.id}`;
    }

    doUpload(value) {
        funcDoUpload.call(this, value);
    }

    get inf_addfile() {
        return require("raw-loader!./interfaces/input-file.add.interface").default;
    }
}

function funcDoUpload(this: InputFile, value) {
    /// initial constants
    const file = value.file;
    const others = { ...value, file: undefined };
    const url = this.$server.UrlFile();
    const headers = this.$server.applyAccessToken();

    /// make loading object
    const loadingObject: IInputFileUnitProcessing = {
        uuid: uniqueId(),
        loading: 0,
    };
    if (!this.multiple) {
        this["pvtInnateValue"] = loadingObject;
    } else {
        this["pvtInnateValue"].push(loadingObject);
    }

    // fieldName is the name of the input field
    // file is the actual file object to send
    const formData = new FormData();
    formData.append("file", file, file.name);

    Object.keys(others).forEach((key) => {
        const value = others[key];
        if (value == undefined) return;
        formData.append(key, others[key]);
    });

    const request = new XMLHttpRequest();
    request.open("POST", url);

    // Iterate over the headers object and set each header
    Object.keys(headers).forEach((headerName) => {
        const headerValue = headers[headerName];
        request.setRequestHeader(headerName, headerValue);
    });

    // Should call the progress method to update the progress to 100% before calling load
    // Setting computable to false switches the loading indicator to infinite mode
    request.upload.onprogress = (e) => {
        loadingObject.loading = e.lengthComputable
            ? Math.ceil((e.loaded / e.total) * 100)
            : true;
    };

    // Should call the load method when done and pass the returned server file id
    // this server file id is then used later on when reverting or restoring a file
    // so your server knows which file to return without exposing that info to the client
    request.onload = () => {
        if (request.status >= 200 && request.status < 300) {
            // the load method accepts either a string (id) or an object
            const responseObject = JSON.parse(request.response);
            if (!this.multiple) {
                this.innateValue = responseObject;
            } else {
                this.innateValue = (this.innateValue as any).map((value) => {
                    if (value.uuid === loadingObject.uuid) {
                        return responseObject;
                    }
                    return value;
                });
            }
        } else {
            // Can call the error method if something is wrong, should exit after
            console.log(`<iv-input-file> request failed. (${url})`);
        }
    };

    request.send(formData);
}
